/**
 * Códec binario del wire (netcode competitivo). DOM-free.
 *
 * Para tiempo real sobre transporte no confiable (UDP / WebTransport datagrams)
 * el tamaño y el coste de (de)serializar importan: JSON de un input ronda ~80
 * bytes; aquí un input son 5 bytes y un snapshot ~46. Menos bytes = menos
 * fragmentación, menos ancho de banda y menos GC en el hot path.
 *
 * Layout little-endian, tamaño fijo (sin strings), apto para ArrayBuffer/datagram.
 */
import { InputState } from "../game";
import { InputCommand, Snapshot, BodyState } from "./protocol";

const INPUT_BYTES = 7; // uint32 tick + uint8 flags + int8 mx + int8 my

function packInput(i: InputState): number {
  return (
    (i.up ? 1 : 0) |
    (i.down ? 2 : 0) |
    (i.left ? 4 : 0) |
    (i.right ? 8 : 0) |
    (i.kick ? 16 : 0) |
    (i.kickRelease ? 32 : 0)
  );
}

function unpackInput(flags: number, mx: number, my: number): InputState {
  return {
    up: (flags & 1) !== 0,
    down: (flags & 2) !== 0,
    left: (flags & 4) !== 0,
    right: (flags & 8) !== 0,
    kick: (flags & 16) !== 0,
    kickRelease: (flags & 32) !== 0,
    mx,
    my,
  };
}

/** Eje analógico a int8: cabe entero porque va acotado a ±100 (ver `InputState.mx`). */
function packAxis(v: number | undefined): number {
  return Math.max(-100, Math.min(100, Math.round(v ?? 0)));
}

/**
 * Paquete de inputs: cuenta + N comandos. Se envía con redundancia (los últimos
 * K inputs) para que un datagrama perdido no pierda un input mientras llegue uno
 * posterior; el servidor deduplica por tick.
 */
export function encodeInputPacket(cmds: readonly InputCommand[]): Uint8Array {
  const n = Math.min(cmds.length, 255);
  const buf = new ArrayBuffer(1 + n * INPUT_BYTES);
  const dv = new DataView(buf);
  dv.setUint8(0, n);
  let o = 1;
  for (let k = cmds.length - n; k < cmds.length; k++) {
    dv.setUint32(o, cmds[k].tick >>> 0, true);
    dv.setUint8(o + 4, packInput(cmds[k].input));
    dv.setInt8(o + 5, packAxis(cmds[k].input.mx));
    dv.setInt8(o + 6, packAxis(cmds[k].input.my));
    o += INPUT_BYTES;
  }
  return new Uint8Array(buf);
}

export function decodeInputPacket(bytes: Uint8Array): InputCommand[] {
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const n = dv.getUint8(0);
  const out: InputCommand[] = [];
  let o = 1;
  for (let k = 0; k < n; k++) {
    out.push({
      tick: dv.getUint32(o, true),
      input: unpackInput(dv.getUint8(o + 4), dv.getInt8(o + 5), dv.getInt8(o + 6)),
    });
    o += INPUT_BYTES;
  }
  return out;
}

function writeBody(dv: DataView, o: number, b: BodyState): number {
  dv.setFloat32(o, b.x, true);
  dv.setFloat32(o + 4, b.y, true);
  dv.setFloat32(o + 8, b.vx, true);
  dv.setFloat32(o + 12, b.vy, true);
  return o + 16;
}

function readBody(dv: DataView, o: number): BodyState {
  return {
    x: dv.getFloat32(o, true),
    y: dv.getFloat32(o + 4, true),
    vx: dv.getFloat32(o + 8, true),
    vy: dv.getFloat32(o + 12, true),
  };
}

/**
 * Snapshot: header (22 B) + N jugadores (16 B c/u) + balón (16 B).
 *
 * NO lleva la ocupación de slots (`Snapshot.active`): este códec solo lo usa el
 * transporte WebTransport legacy, que es de 2 jugadores con bot server-side —
 * ahí los dos slots están siempre ocupados y el cliente asume justo eso cuando
 * el campo falta. Si el códec pasa a usarse en salas de N slots, hay que añadir
 * un bitmask al header (y subir la versión del layout).
 * El header incluye el reloj autoritativo (matchTime + resetTimer, float32) para
 * que el cliente no re-cuente el tiempo al reconciliar.
 * Posiciones en float32 (suficiente para render; la autoridad sigue en float64).
 */
export function encodeSnapshot(s: Snapshot): Uint8Array {
  const n = s.players.length;
  const buf = new ArrayBuffer(22 + n * 16 + 16);
  const dv = new DataView(buf);
  dv.setUint32(0, s.serverTick >>> 0, true);
  dv.setInt32(4, s.ackTick | 0, true);
  const flags =
    (s.over ? 1 : 0) |
    (s.winner === 0 || s.winner === 1 ? 2 : 0) |
    (s.winner === 1 ? 4 : 0);
  dv.setUint8(8, flags);
  dv.setUint16(9, s.score[0] & 0xffff, true);
  dv.setUint16(11, s.score[1] & 0xffff, true);
  dv.setUint8(13, n);
  dv.setFloat32(14, s.matchTime ?? 0, true);
  dv.setFloat32(18, s.resetTimer ?? 0, true);
  let o = 22;
  for (const p of s.players) o = writeBody(dv, o, p);
  writeBody(dv, o, s.ball);
  return new Uint8Array(buf);
}

export function decodeSnapshot(bytes: Uint8Array): Snapshot {
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const serverTick = dv.getUint32(0, true);
  const ackTick = dv.getInt32(4, true);
  const flags = dv.getUint8(8);
  const score: [number, number] = [dv.getUint16(9, true), dv.getUint16(11, true)];
  const n = dv.getUint8(13);
  const matchTime = dv.getFloat32(14, true);
  const resetTimer = dv.getFloat32(18, true);
  let o = 22;
  const players: BodyState[] = [];
  for (let k = 0; k < n; k++) { players.push(readBody(dv, o)); o += 16; }
  const ball = readBody(dv, o);
  const over = (flags & 1) !== 0;
  const winner = (flags & 2) === 0 ? null : (flags & 4) !== 0 ? 1 : 0;
  return { serverTick, ackTick, players, ball, score, over, winner, matchTime, resetTimer };
}
